import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, MenuItem,
  Select, List, Typography, Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import {
  DialogTitleStyled,
  CloseIconButtonStyled,
  StyledTextField,
  InProgressListWrapper,
  InProgressListTitle,
  ReassignActionBox,
  BlueContainedButton,
  RedContainedButton,
  StyledListItemCard,
  StyledListItemTextBox,
  ButtonGroupBox,
  ReassignSmallButton
} from '../../styles/CalculateMoney/AssignmentCustomerModalStyles';
import { useWorkSessionsContext } from '../../context/WorkSessionsContext';
import ConfirmationDialog from '../../components/ConfirmationDialog';

const AssignCustomerModal = ({
  open, onClose, staff, businessId, onShowSuccess,
  inProgressSessions = [], staffList = []
}) => {
  const { t } = useTranslation('assignCustomerModal');
  const [form, setForm] = useState({ customerName: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [reassigningId, setReassigningId] = useState(null);
  const [targetStaffId, setTargetStaffId] = useState('');
  const [deletingSessionId, setDeletingSessionId] = useState(null);

  const {
    createWorkSessionAndUpdateList,
    fetchWorkSessionsByBusinessData,
    updateWorkSessionAndRefresh,
    deleteWorkSessionAndRefresh
  } = useWorkSessionsContext();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    setSubmitting(true);
    const sessionData = {
      businessId: businessId,
      customerName: form.customerName,
      staffId: staff.staffId,
      note: form.note || t('defaultNote')
    };
    try {
      await createWorkSessionAndUpdateList(sessionData);
      await fetchWorkSessionsByBusinessData(businessId);
      setForm({ customerName: '', note: '' });
      if (onShowSuccess)
        onShowSuccess(
          t('successMessage', { customerName: form.customerName, staffName: staff.name })
        );
      onClose();
    } catch (err) { } finally { setSubmitting(false); }
  };

  const handleStartReassign = (sessionId) => {
    setReassigningId(sessionId);
    setTargetStaffId('');
  };

  const handleReassign = async (sessionId) => {
    if (!targetStaffId) return;
    setSubmitting(true);
    try {
      await updateWorkSessionAndRefresh(sessionId, { staffId: targetStaffId }, businessId);
      await fetchWorkSessionsByBusinessData(businessId);
      if (onShowSuccess) onShowSuccess(t('successReassign'));
      setReassigningId(null);
      setTargetStaffId('');
      onClose();
    } catch (err) { } finally { setSubmitting(false); }
  };

  const handleConfirmDelete = async () => {
    setSubmitting(true);
    try {
      await deleteWorkSessionAndRefresh(deletingSessionId, businessId);
      if (onShowSuccess) onShowSuccess(t('successDelete'));
      setDeletingSessionId(null);
      setReassigningId(null);
      setTargetStaffId('');
    } catch (err) { } finally { setSubmitting(false); }
  };

  const handleCancelDelete = () => {
    setDeletingSessionId(null);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitleStyled>
        {t('title', { staffName: staff?.name })}
        <CloseIconButtonStyled aria-label="close" onClick={onClose}>
          <CloseIcon />
        </CloseIconButtonStyled>
      </DialogTitleStyled>
      <DialogContent dividers>
        <StyledTextField
          autoFocus
          margin="dense"
          name="customerName"
          label={t('fields.customerName')}
          type="text"
          fullWidth
          value={form.customerName}
          onChange={handleChange}
        />
        <StyledTextField
          margin="dense"
          name="note"
          label={t('fields.note')}
          type="text"
          fullWidth
          value={form.note}
          onChange={handleChange}
        />

        {/* In Progress Sessions List */}
        <InProgressListWrapper>
          <InProgressListTitle>
            {t('inProgressSessions', { staffName: staff.name })}
          </InProgressListTitle>
          <List dense>
            {inProgressSessions.map(session => (
              <React.Fragment key={session.workSessionId}>
                <StyledListItemCard>
                  <StyledListItemTextBox>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {session.customerName || t('unnamed')}
                    </Typography>
                    <Typography variant="body2" color="primary">
                      {session.sessionStart && new Date(session.sessionStart).toLocaleString()}
                    </Typography>
                  </StyledListItemTextBox>
                  <ButtonGroupBox>
                    <BlueContainedButton
                      size="small"
                      onClick={() => handleStartReassign(session.workSessionId)}
                      disabled={reassigningId === session.workSessionId}
                    >
                      {t('buttons.reassign')}
                    </BlueContainedButton>
                    <RedContainedButton
                      size="small"
                      onClick={() => setDeletingSessionId(session.workSessionId)}
                      disabled={submitting}
                    >
                      {t('buttons.delete')}
                    </RedContainedButton>
                  </ButtonGroupBox>
                </StyledListItemCard>
                {reassigningId === session.workSessionId && (
                  <ReassignActionBox>
                    <Select
                      size="small"
                      value={targetStaffId}
                      onChange={e => setTargetStaffId(e.target.value)}
                      displayEmpty
                      sx={{ minWidth: 120, bgcolor: "#fff" }}
                    >
                      <MenuItem value="" disabled>{t('fields.selectStaff')}</MenuItem>
                      {staffList
                        .filter(s => s.staffId !== staff.staffId)
                        .map(s => (
                          <MenuItem key={s.staffId} value={s.staffId}>{s.name}</MenuItem>
                        ))}
                    </Select>
                    <ReassignSmallButton
                      variant="contained"
                      size="small"
                      color="primary"
                      disabled={!targetStaffId || submitting}
                      onClick={() => handleReassign(session.workSessionId)}
                    >
                      {t('buttons.confirm')}
                    </ReassignSmallButton>
                    <ReassignSmallButton
                      size="small"
                      color="inherit"
                      onClick={() => setReassigningId(null)}
                      disabled={submitting}
                    >
                      {t('buttons.cancel')}
                    </ReassignSmallButton>
                  </ReassignActionBox>
                )}
              </React.Fragment>
            ))}
          </List>
        </InProgressListWrapper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>{t('buttons.cancel')}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={submitting || !form.customerName}
        >
          {t('buttons.assign')}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        open={!!deletingSessionId}
        title={t('deleteTitle')}
        content={t('deleteContent')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Dialog>
  );
};
export default AssignCustomerModal;